import { DropletType } from '@class/units/endpoint/endpoint-payload.model';

/**
 * Convenice class to handle V4 Droplet specific logic and capabilities.
 *
 * This is not an exhaustive list of capabilities but rather the ones we've identified need to be
 * handled differently in V4 Droplets compared to classic physical or cloud Droplets.
 */
export class DropletV4 {
  /**
   * Checks if the droplet is a V4 droplet.
   *
   * @param dropletType - The type of the droplet.
   * @param unitId - The ID of the unit to check whether the unit is marked as a V4 for dev purposes.
   * @returns true if the Droplet is a V4 droplet, false otherwise.
   */
  static isV4(dropletType: DropletType | string, unitId: string = null): boolean {
    return dropletType === DropletType.DROPLET_V4 || this.isForcedV4(unitId);
  }

  /**
   * Checks if the unit is marked as a V4 in localStorage for dev purposes.
   *
   * @param unitId - The ID of the unit to check whether the unit is marked as a V4 for dev purposes.
   * @returns true if the unit is marked as a V4 for dev purposes, false otherwise.
   */
  static isForcedV4(unitId: string): boolean {
    return (JSON.parse(window.localStorage.getItem('v4Units')) ?? []).includes(unitId);
  }

  /**
   * V4 Droplets do not emit their status through MQTT.
   *
   * @returns true if the Droplet's status can be retrieved via MQTT, false otherwise.
   */
  static showMqttDropletStatus(): boolean {
    return false;
  }

  /**
   * V4 devices do not emit their status through MQTT.
   *
   * @returns true if the device's status can be retrieved via MQTT, false otherwise.
   */
  static showMqttDeviceStatus(): boolean {
    return false;
  }

  /**
   * V4 Droplets do not support adding devices through PWA.
   * This is managed through configuration at the moment.
   *
   * @returns true if devices can be added via UI, false otherwise.
   */

  static canAddDevices(): boolean {
    return false;
  }

  /**
   * V4 Droplets do not support removing devices through PWA.
   * This is managed through configuration at the moment.
   *
   * @returns true if devices can be removed via UI, false otherwise.
   */
  static canRemoveDevices(): boolean {
    return false;
  }

  /**
   * V4 Droplets do not support editing devices through PWA.
   * This is managed through configuration at the moment.
   *
   * @returns true if devices can be edited via UI, false otherwise.
   */
  static canEditDevices(): boolean {
    return false;
  }

  /**
   * Controller inventory comes from MQTT at the moment. V4 Droplets do not support this.
   *
   * @returns true if controllers can be listed in the UI, false otherwise.
   */
  static canViewControllers(): boolean {
    return false;
  }

  /**
   * Controller inventory is not supported in V4 Droplets as it comes from MQTT.
   *
   * @returns true if the controller inventory can be requested, false otherwise.
   */
  static canUseControllerInventory(): boolean {
    return false;
  }

  /**
   * Firmware updates through PWA are not supported in V4 Droplets.
   *
   * @returns true if firmware can be updated via UI, false otherwise.
   */
  static canUpdateFirmware(): boolean {
    return false;
  }

  /**
   * Energy tab in the overview section is not supported in V4 Droplets.
   *
   * @returns true if energy tab can be viewed, false otherwise.
   */
  static canViewEnergyTab(): boolean {
    return false;
  }

  /**
   * Settings tab is not supported in V4 Droplets.
   *
   * @returns true if settings tab can be viewed, false otherwise.
   */
  static canViewSettingsTab(): boolean {
    return false;
  }
}
